import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Flame, Sparkles } from "lucide-react";
import { getVisitorId } from "@/lib/tracking";
import { useAssistant, getAssistantSessionId } from "@/lib/assistant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHIPS = ["Is the 2-bag bundle worth it?", "How do I apply it?", "Is it safe for pets?", "How does it protect?"];

export default function SalesAssistant() {
  const { open, setOpen, seed, clearSeed } = useAssistant();
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hey — I'm the NOXWAY assistant. Ask me anything about pricing, coverage, application or safety." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (seed) {
      setMessages((m) => [...m, { from: "bot", text: seed, proactive: true }]);
      clearSeed();
    }
  }, [seed, clearSeed]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const ask = async (question) => {
    if (!question.trim() || busy) return;
    setMessages((m) => [...m, { from: "user", text: question }, { from: "bot", text: "" }]);
    setInput("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/assistant/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          visitor_id: getVisitorId(),
          session_id: getAssistantSessionId(),
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, text: last.text + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { from: "bot", text: "Something glitched — try again, or email sales@noxway.shop." };
        return copy;
      });
    }
    setBusy(false);
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(!open)}
        data-testid="assistant-toggle"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#f97316] to-[#ea580c] shadow-[0_8px_36px_rgba(255,98,31,0.5)] flex items-center justify-center"
        aria-label="Open NOXWAY assistant"
      >
        {open ? <X size={22} className="text-white" /> : <MessageCircle size={22} className="text-white" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="card-glow-border rounded-2xl bg-[#0f1117]/97 backdrop-blur-xl overflow-hidden flex flex-col"
            style={{ position: "fixed", right: 20, bottom: 96, zIndex: 50, width: "min(384px, calc(100vw - 40px))", maxHeight: "min(520px, 70vh)" }}
            data-testid="assistant-panel"
          >
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-white/10">
              <Flame size={16} className="text-[#f97316]" />
              <span className="font-display font-bold tracking-[0.12em] text-sm">NOXWAY ASSISTANT</span>
              <span className="ml-auto inline-flex items-center gap-1 font-tech text-[8px] tracking-[0.15em] uppercase text-[#f97316] border border-[#f97316]/30 rounded-full px-2 py-0.5">
                <Sparkles size={9} /> AI
              </span>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="assistant-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] text-sm leading-relaxed rounded-2xl px-4 py-2.5 ${
                    m.from === "user" ? "bg-[#f97316]/20 text-white" : "bg-white/5 text-slate-300"
                  }`}>
                    {m.text}
                    {m.proactive && (
                      <a href="#buy" onClick={() => setOpen(false)}
                        className="block mt-2 font-tech text-[9px] tracking-[0.15em] uppercase text-[#f97316] hover:underline"
                        data-testid="assistant-proactive-cta">
                        See the bundle deal →
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {busy && messages[messages.length - 1]?.text === "" && (
                <div className="text-xs text-slate-500 font-tech">thinking…</div>
              )}
              <div ref={endRef} />
            </div>
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {CHIPS.map((c) => (
                <button key={c} onClick={() => ask(c)} data-testid="assistant-chip"
                  className="font-tech text-[9px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-[#f97316]/30 text-[#f97316] hover:bg-[#f97316]/10 transition-colors">
                  {c}
                </button>
              ))}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); ask(input); }}
              className="flex items-center gap-2 px-4 py-3 border-t border-white/10"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about NOXWAY…"
                data-testid="assistant-input"
                className="flex-1 bg-white/5 border border-white/15 rounded-full px-4 py-2.5 text-sm focus:border-[#f97316] outline-none"
              />
              <button type="submit" data-testid="assistant-send"
                className="w-10 h-10 rounded-full bg-gradient-to-br from-[#f97316] to-[#ea580c] flex items-center justify-center shrink-0">
                <Send size={15} className="text-white" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
